import { useState, useEffect, useRef } from 'react'
import debounce from 'lodash/debounce'
import styled from '@emotion/styled'
import SideModal from '../Modal/SideModal'
import { useModal, useShop, useConsumerCart, useCheckout } from 'shop/hooks'
import ThreeDotsLoader from 'shop/components/Loader/ThreeDotsLoader'
import { TrackableEvent } from 'tracker'
import { trackUserActionsFBPixel, trackUserActionsGA4 } from 'tracker'
import { useNavigate } from 'react-router-dom'
import {
  createEcommEventDataFromConsumerCart,
  merchantGA4EcommTrackViewCart,
  slerpGA4EcommTrackViewCart
} from 'tracker/GA/ecommerce'
import {
  CartErrorMessage,
  OrderItemWithVatPrices,
  OrderItem
} from 'shop/types/cart'
import { formatMoney, getCurrentProductErrors, getInvalidItems } from './utils'
import CartProductErrorModal from '../Modal/CartProductErrorModal/CartProductErrorModal'
import { CART_MODAL_ORIGIN } from 'shop/types'
import { StyledHTMLElement } from 'shop/theme/types'
import { Medium } from '../common/fontSizeTags'
import CartModalHeader from './CartModalHeader'
import EmptyCartState from './EmptyCartState'
import CartModalItemList from './CartModalItemList'
import CartModalFooter from './CartModalFooter'
import { LineBreak } from '../Shop/commonStyles'
import { AlcoholNote } from '../Cart/AlcoholNote'

interface Props {
  closeCartModal: () => void
}

const CartModal = ({ closeCartModal }: Props) => {
  const { closeModal } = useModal()
  const navigate = useNavigate()
  const { partner } = useShop()
  const { cart, validateCart, errors, cartLoading } = useConsumerCart()
  const {
    productError,
    minimumOrderValueWarning,
    dynamicPrepTimeSlotValidation,
    hasAlcoholicProducts,
    handleOrderItemQuantityChange
  } = useCheckout()
  const [invalidOrderItems, setInvalidOrderItems] = useState<
    OrderItemWithVatPrices[]
  >([])
  const [unavailableOrderItems, setUnavailableOrderItems] = useState<
    OrderItem[]
  >([])
  const [validOrderItems, setValidOrderItems] = useState<OrderItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showProductErrorModal, setShowProductErrorModal] = useState(false)
  const [retryCallback, setRetryCallback] = useState<
    ((proceedWithNewOrderItems: boolean) => void) | null
  >(null)
  const [navBottomPosition, setNavBottomPosition] = useState('0px')

  // Store accumulated quantity change deltas per order item, see debouncedQuantityChange
  const pendingDeltas = useRef<Record<string, number>>({})

  const isMounted = useRef(true)

  const orderItems = cart?.orderItems
  const storeName = cart?.store.name

  const showEmptyState =
    !isLoading &&
    !orderItems?.length &&
    !invalidOrderItems.length &&
    !unavailableOrderItems.length

  /** Runs once on mountsetting isMounted & cleans up on unmount */
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  /** Calls validateCart on init to get latest errors/warnings */
  useEffect(() => {
    validateCart(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Ensure cart modal is below navbar
  useEffect(() => {
    const updateNavPosition = () => {
      const navBar = document.getElementById('nav-bar-component')
      if (navBar) {
        const rect = navBar.getBoundingClientRect()
        const bottomPosition = rect.bottom.toString()
        setNavBottomPosition(bottomPosition + 'px')
      }
    }

    // Initial position
    updateNavPosition()

    // Update on resize
    const resizeObserver = new ResizeObserver(updateNavPosition)
    const navBar = document.getElementById('nav-bar-component')
    if (navBar) {
      resizeObserver.observe(navBar)
    }

    // Cleanup resize observer
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  /** Handles tracking on init */
  useEffect(() => {
    if (partner?.name && cart?.id) {
      if (!isMounted.current) return

      const { id, orderItems, store } = cart
      const { name: storeName } = store

      const eventData = {
        cart_id: id,
        products: orderItems.map((item) => ({
          product_id: item.variantId,
          name: item.name,
          currency: 'GBP',
          quantity: item.quantity,
          price: item.total.discounted || item.total.base
        }))
      }

      const ecommerceEventData = createEcommEventDataFromConsumerCart(
        cart,
        partner?.name,
        storeName
      )

      const body = {
        category: 'Cart',
        action: TrackableEvent.CartViewed
      }

      trackUserActionsGA4(body, 'slerpGA4Tracking')

      // legacy tracking
      trackUserActionsFBPixel(TrackableEvent.CartViewed, eventData)
      trackUserActionsGA4(body, 'merchantGA4Tracking')

      slerpGA4EcommTrackViewCart(ecommerceEventData)
      merchantGA4EcommTrackViewCart(ecommerceEventData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id, partner?.name])

  /** Sets order items to render in UI from consumer product validations */
  useEffect(() => {
    if (!orderItems) return

    if (errors.length) {
      setIsLoading(true)
      const currentProductErrors = getCurrentProductErrors(errors)

      if (!currentProductErrors.length) {
        resetOrderItemStates()
        setIsLoading(false)
        return
      }

      if (currentProductErrors.length) {
        let invalidOrderItems: (OrderItem | OrderItemWithVatPrices)[] = []
        let unavailableOrderItems: (OrderItem | OrderItemWithVatPrices)[] = []

        // Set invalid and unavailable order items based upon cart error messages
        currentProductErrors.forEach((productError) => {
          const items = getInvalidItems(productError, orderItems)
          if (
            productError.message === CartErrorMessage.PRODUCT_PRICES_CHANGED
          ) {
            invalidOrderItems.push(...items)
            // PRODUCT_LIMIT_REACHED handled more globally in ShopBody.tsx
          } else if (
            productError.message !== CartErrorMessage.PRODUCT_LIMIT_REACHED
          ) {
            // INVALID_MODIFIERS, PRODUCT_UNAVAILABLE, PRODUCTS_OUT_OF_STOCK
            unavailableOrderItems.push(...items)
          }
        })

        setInvalidOrderItems(invalidOrderItems as OrderItemWithVatPrices[])
        setUnavailableOrderItems(unavailableOrderItems)

        const invalidAndUnavailableIds = [
          ...invalidOrderItems.map((item) => item.id),
          ...unavailableOrderItems.map((item) => item.id)
        ]

        const currentValidOrderItems = orderItems.filter(
          (orderItem: OrderItem) =>
            !invalidAndUnavailableIds.includes(orderItem.id)
        )

        setValidOrderItems(currentValidOrderItems)
        setIsLoading(false)
      }
    } else {
      setIsLoading(true)
      resetOrderItemStates()
      setIsLoading(false)
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, orderItems])

  const resetOrderItemStates = () => {
    setInvalidOrderItems([])
    setUnavailableOrderItems([])
    setValidOrderItems(orderItems || [])
  }

  const checkout = () => {
    if (!cart?.id) return
    closeModal('cart')
    return navigate('/checkout')
  }

  const hasCheckoutWarnings =
    !!minimumOrderValueWarning || !!dynamicPrepTimeSlotValidation

  const hasUnavailableInvalidItems =
    !!unavailableOrderItems.length || !!invalidOrderItems.length

  const disableCheckout = hasUnavailableInvalidItems || hasCheckoutWarnings

  const onContinue = () => {
    // Handles retry behaviour for other product errors
    if (retryCallback) {
      retryCallback(true)
    }
  }

  const debouncedQuantityChange = debounce(
    async (orderItem: OrderItem, proceedWithNewOrderItems?: boolean) => {
      const accumulatedDelta = pendingDeltas.current[orderItem.id] || 0

      // Clear the pending delta before making the call
      delete pendingDeltas.current[orderItem.id]

      const res = await handleOrderItemQuantityChange(
        orderItem,
        accumulatedDelta,
        proceedWithNewOrderItems
      )
      const { errors } = res || {}

      if (!errors?.length) {
        setShowProductErrorModal(false)
        return
      }

      setRetryCallback(
        () => (proceedWithNewOrderItems?: boolean) =>
          handleOrderItemQuantityChange(
            orderItem,
            accumulatedDelta,
            proceedWithNewOrderItems
          ).then(() => setShowProductErrorModal(false))
      )

      setShowProductErrorModal(true)
    },
    500
  )

  /** Accumulate the delta for the specific order item, trigger debounce function */
  const handleQuantityChange = async (
    orderItem: OrderItem,
    delta: number,
    proceedWithNewOrderItems?: boolean
  ): Promise<void> => {
    pendingDeltas.current[orderItem.id] =
      (pendingDeltas.current[orderItem.id] || 0) + delta

    // Trigger the debounced function
    return debouncedQuantityChange(orderItem, proceedWithNewOrderItems)
  }

  // Cleanup debounced function on teardown
  useEffect(() => {
    return () => {
      debouncedQuantityChange.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ctaCopy = () => {
    if (minimumOrderValueWarning) {
      const { base: subtotal } = cart?.summary.subtotal || {}
      const minimumValue = minimumOrderValueWarning.minimumValue
      const amountToCheckout =
        parseFloat(minimumValue) - parseFloat(subtotal || '0')
      return `Spend ${formatMoney(amountToCheckout)} more to checkout`
    }
    if (dynamicPrepTimeSlotValidation?.restriction?.maxPossibleBasketValue) {
      const maxPossibleBasketValue =
        dynamicPrepTimeSlotValidation.restriction.maxPossibleBasketValue
      return (
        <DynamicPrepTimeCtaSpan>
          Order too large to prepare before closing.{' '}
          <Medium>
            Reduce to under {formatMoney(maxPossibleBasketValue)} to checkout.
          </Medium>
        </DynamicPrepTimeCtaSpan>
      )
    }
    return `Go to checkout`
  }

  return (
    <>
      {productError && showProductErrorModal && storeName && (
        <CartProductErrorModal
          loading={cartLoading}
          invalidItems={unavailableOrderItems}
          storeName={storeName}
          onContinue={onContinue}
          origin={CART_MODAL_ORIGIN}
          productError={productError}
        />
      )}
      <SideModal handleCloseModal={closeCartModal} top={navBottomPosition}>
        <CartModalHeader />

        {showEmptyState && <EmptyCartState closeModal={closeModal} />}

        {hasAlcoholicProducts && (
          <>
            <IdRequiredContainer>
              <AlcoholNote />
            </IdRequiredContainer>
            <LineBreakContainer>
              <LineBreak height={'2px'} />
            </LineBreakContainer>
          </>
        )}

        <CartItemListContainer>
          <CartModalItemList
            validOrderItems={validOrderItems}
            invalidOrderItems={invalidOrderItems}
            unavailableOrderItems={unavailableOrderItems}
            onQuantityChange={handleQuantityChange}
          />
        </CartItemListContainer>

        {!!validOrderItems.length && (
          <CartModalFooter
            cart={cart}
            cartLoading={cartLoading}
            disableCheckout={disableCheckout}
            hasCheckoutWarnings={hasCheckoutWarnings}
            checkout={checkout}
            ctaCopy={ctaCopy}
          />
        )}
        {/** Add below condition to (isLoading || cartLoading) when cart is on Shop page */}
        {isLoading && (
          <LoadingContainer>
            <ThreeDotsLoader />
          </LoadingContainer>
        )}
      </SideModal>
    </>
  )
}

const DynamicPrepTimeCtaSpan = styled.span<StyledHTMLElement>({
  whiteSpace: 'normal',
  textAlign: 'center',
  fontWeight: 400
})

const CartItemListContainer = styled.div<StyledHTMLElement>({
  marginTop: '16px'
})

const LoadingContainer = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
})

const IdRequiredContainer = styled.div({
  marginBottom: '16px'
})

const LineBreakContainer = styled.div<StyledHTMLElement & { margin?: string }>(
  ({ margin = '0 -16px 0px' }) => ({
    margin: margin
  })
)

export default CartModal
